import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import Button from "components/Button";
import ColorField from "components/ColorField";
import HollowButton from "components/HollowButton";
import { IEbiMailTheme } from "dto/Ebi/IEbiOffice";
import { sameMailTheme } from "utils/ebiMailTheme";

import styles from "./styles.module.scss";
import { FC, IEbiMailThemeSectionProps } from "./typings";

// Kolorystyka maili EBI (Powiadomienia): kafelki gotowych wariantów (miniatura: tło, biała karta, przycisk, stopka) i trzy
// własne kolory. Stan trzyma zakładka, bo podgląd obok pokazuje kolory jeszcze niezapisane; zapis idzie do ustawień EBI.

/** Kolory miniatury wariantu jako zmienne CSS — tła ustawia arkusz. */
const sampleStyle = (theme: IEbiMailTheme): CSSProperties =>
  ({
    "--sample-background": theme.Background,
    "--sample-button": theme.ButtonColor,
    "--sample-footer": theme.FooterBackground,
  }) as CSSProperties;

const EbiMailThemeSection: FC<IEbiMailThemeSectionProps> = ({
  settings,
  value,
  dirty,
  isSaving,
  error,
  onChange,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const presets = settings?.Presets ?? [];
  const canEdit = !!settings?.CanEdit;
  const canSave = canEdit && !!settings?.HasSettings;
  const activeKey = value ? presets.find((preset) => sameMailTheme(preset, value))?.Key ?? null : null;

  const choosePreset = (preset: IEbiMailTheme) => {
    if (!canEdit) return;

    onChange({
      Background: preset.Background,
      ButtonColor: preset.ButtonColor,
      FooterBackground: preset.FooterBackground,
    });
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{t("ebiOffice.notifications.theme.title")}</div>
      <div className={styles.hint}>{t("ebiOffice.notifications.theme.description")}</div>
      {error && <div className={styles.error}>{error}</div>}
      {settings && !canEdit && <div className={styles.hint}>{t("ebiOffice.notifications.theme.readOnly")}</div>}
      {settings && canEdit && !settings.HasSettings && (
        <div className={styles.hint}>{t("ebiOffice.notifications.theme.needsSettings")}</div>
      )}

      {value && presets.length > 0 && (
        <>
          <div className={styles.presetGrid}>
            {presets.map((preset) => (
              <div
                key={preset.Key}
                className={classNames(
                  styles.preset,
                  preset.Key === activeKey && styles.presetActive,
                  !canEdit && styles.presetDisabled,
                )}
                title={preset.Name}
                onClick={() => choosePreset(preset)}
              >
                <div className={styles.presetSample} style={sampleStyle(preset)}>
                  <div className={styles.presetCard}>
                    <span className={styles.presetButton} />
                    <span className={styles.presetFooter} />
                  </div>
                </div>
                <div className={styles.presetName}>
                  {t(`ebiOffice.notifications.theme.preset.${preset.Key}`, { defaultValue: preset.Name })}
                </div>
              </div>
            ))}
          </div>
          {!activeKey && <div className={styles.hint}>{t("ebiOffice.notifications.theme.customHint")}</div>}

          <div className={styles.colorFields}>
            <ColorField
              label={t("ebiOffice.notifications.theme.background")}
              value={value.Background}
              onChange={(color) => onChange({ ...value, Background: color })}
              disabled={!canEdit}
            />
            <ColorField
              label={t("ebiOffice.notifications.theme.button")}
              value={value.ButtonColor}
              onChange={(color) => onChange({ ...value, ButtonColor: color })}
              disabled={!canEdit}
            />
            <ColorField
              label={t("ebiOffice.notifications.theme.footer")}
              value={value.FooterBackground}
              onChange={(color) => onChange({ ...value, FooterBackground: color })}
              disabled={!canEdit}
            />
          </div>

          {canEdit && (
            <div className={styles.actions}>
              {dirty && (
                <HollowButton
                  text={t("ebiOffice.notifications.theme.cancel")}
                  onClick={onCancel}
                  disabled={isSaving}
                />
              )}
              <Button
                text={t("ebiOffice.notifications.theme.save")}
                onClick={onSave}
                disabled={!dirty || !canSave || isSaving}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EbiMailThemeSection;
